import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";

export interface RevertPreviousValue {
  label?: string;
  value: ReactNode;
}

export interface RevertConfirmationDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isPending?: boolean;
  error?: string | null;
  /** Bolded field name used by the default prompt. */
  fieldLabel?: ReactNode;
  /** Replaces the default "revert the X field?" prompt. */
  prompt?: ReactNode;
  previousValues?: RevertPreviousValue[];
  previousValuesIntro?: ReactNode;
}

export default function RevertConfirmationDialog({
  open,
  onCancel,
  onConfirm,
  isPending = false,
  error,
  fieldLabel,
  prompt,
  previousValues,
  previousValuesIntro = "This will restore it to its previous value:",
}: Readonly<RevertConfirmationDialogProps>) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="revert-dialog-title"
      aria-describedby="revert-dialog-description"
    >
      <DialogTitle id="revert-dialog-title">Confirm Revert</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography
          id="revert-dialog-description"
          variant="body2"
          sx={{ mb: 2 }}
        >
          {prompt ?? (
            <>
              Are you sure you want to revert the{" "}
              <strong>{fieldLabel}</strong> field?
            </>
          )}
        </Typography>
        {previousValues && previousValues.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary">
              {previousValuesIntro}
            </Typography>
            <Box
              sx={{
                mt: 1,
                p: 1,
                backgroundColor: "action.hover",
                borderRadius: 1,
              }}
            >
              {previousValues.map((previous, index) => {
                const label = previous.label ?? "Previous value";
                const value = previous.value;
                const displayValue =
                   value === null || value === undefined || value === ""
                     ? ""
                     : value;
                return (
                  <Typography
                    key={`${label}-${index}`}
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    <strong>{label}:</strong> {displayValue}
                  </Typography>
                );
              })}
            </Box>
          </>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          The revert action will be recorded in the activity log and can itself
          be reverted.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={isPending}
        >
          {isPending ? "Reverting..." : "Revert"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
